/**
 * I FILE GIA' LETTI SOPRAVVIVONO ALLA RICARICA (STU-424)
 *
 * Il 23/09 una studentessa sceglie una foto sulla Home. Il server la legge in
 * 8 s e 0,33 s dopo l'app riparte da capo. La coda dei file scelti
 * (`processedNotes` in Redux) viveva solo in memoria: la foto letta e'
 * sparita e lei ha trovato la card vuota. Su quel browser l'app e' ripartita
 * 10 volte in 48 minuti senza nessun rilascio, e dal 15/09 8 scelte perse su
 * 35 sono ricariche. Il documento nasce solo al clic su «Genera»
 * (handleGenerateExam): fino a quel momento l'unica copia del lavoro fatto e'
 * la coda. Qui la coda si copia in IndexedDB e al rientro torna nella card,
 * con la riga che c'e' gia' («Il file non e' ancora caricato: premi Genera»).
 *
 * COSA SI SALVA. Solo gli elementi letti davvero: testo estratto non vuoto,
 * non falliti (`estrazioneFallita`, accordo con STU-424 parte 1, o
 * l'`errorMessage` di oggi) e con un'identita'. Solo dati semplici, gli
 * stessi che handleGenerateExam e getExtractedQA leggono: nome, tipo,
 * dimensione, testo, tag, metadati, sorgente dell'URL. Il campo `files` delle
 * foto (gli oggetti File originali) resta fuori: nessuno lo legge dopo
 * l'estrazione. Un elemento con un altro File o Blob dentro non si salva
 * affatto, perche' non si puo' sapere se gli serve. I file grandi non passano
 * mai dalla coda (handleLargeFileUpload li porta via subito).
 *
 * PER CHI E PER QUANTO. Una voce per uid; al ripristino si cancellano quelle
 * degli altri account, quindi sul browser resta al massimo la coda di chi sta
 * usando l'app. Ogni file scade 24 ore dopo la prima volta che e' stato
 * salvato. All'uscita dall'account (useSignOut) si cancella tutto.
 *
 * LA TRAPPOLA. Al rientro lo stato iniziale e' una coda vuota, e la Home al
 * mount la azzera (RESET_PROCESSED_NOTES_UNLESS_PENDING). Se si scrivesse a
 * ogni cambio, quella coda vuota cancellerebbe il salvato prima che il
 * ripristino lo legga. Per questo non si scrive niente finche' il ripristino
 * per l'uid attuale non e' finito; se la lettura fallisce non si scrive per
 * tutta la sessione.
 *
 * QUANDO SI SVUOTA. Dopo «Genera» la coda resta in Redux con `isProcessing`
 * alto finche' la Home non rimonta: e' lo stesso segnale con cui il reducer
 * (STU-362) riconosce una coda consumata, e qui vale come coda vuota, scritta
 * subito e non col ritardo. Togliendo i file il salvato segue.
 *
 * DURANTE «GENERA». `isProcessing` si alza solo dopo l'ultimo setDoc, e con
 * testi grandi anche dopo il caricamento su Storage: una ricarica in quella
 * finestra riportava nella card file che erano gia' documenti, con «Genera»
 * acceso, e il secondo clic li raddoppiava (e chi e' Base pagava la quota due
 * volte). Per questo la copia si cancella al clic, prima del primo documento
 * (sospendiCodaFileSalvata), e niente la riscrive finche' la generazione non
 * ha un esito: riuscita, la coda e' consumata e resta vuota; fallita, il
 * catch la riscrive com'e' (riprendiCodaFileSalvata), perche' lo studente
 * deve poterla ancora generare. Prima della coda salvata una ricarica in quel
 * momento perdeva i file ma non faceva doppioni: si torna a quello.
 *
 * Niente qui dentro puo' far fallire un caricamento: ogni errore di
 * IndexedDB finisce nel nulla e l'app si comporta come prima.
 */
import { ADD_PROCESSED_NOTE, SET_GENERATE, RESET_PROCESSED_NOTES } from '@redux/actions/types';
import { chiaveFile } from '@services/cronometroFasi';
import { trackEvent } from '@utils/analytics';

export const SCADENZA_MS = 24 * 60 * 60 * 1000;
/** Breve: raccoglie i file di una scelta multipla (un dispatch per file nello stesso giro) senza lasciare scoperta la ricarica che arriva 0,33 s dopo. */
export const RITARDO_SCRITTURA_MS = 50;
export const EVENTO_RIPRISTINO = 'upload_queue_restored';
export const VERSIONE_FORMATO = 1;
/** Quanto «Genera» aspetta al massimo che la copia sia cancellata: un IndexedDB bloccato non lo ferma. */
export const ATTESA_CANCELLAZIONE_MS = 300;

const NOME_DB = 'studierai-coda-file';
const VERSIONE_DB = 1;
const NOME_ARCHIVIO = 'code';
const PROFONDITA_MAX = 32;

// ---------------------------------------------------------------------------
// Identita' e filtro: funzioni pure
// ---------------------------------------------------------------------------

/**
 * L'identita' che gli elementi hanno gia': `id` quando c'e', l'URL per gli
 * estratti da link (REMOVE_PROCESSED_NOTE), altrimenti nome + byte, la stessa
 * del cronometro e della cache documenti. Serve a non creare doppioni al
 * ripristino.
 */
export const identitaElemento = (elemento) => {
  if (!elemento || typeof elemento !== 'object') return null;
  const { id } = elemento;
  if ((typeof id === 'string' && id) || Number.isFinite(id)) return `id:${id}`;
  if (elemento.type === 'url-extract' && typeof elemento.source_url === 'string' && elemento.source_url) {
    return `url:${elemento.source_url}`;
  }
  const chiave = chiaveFile(elemento);
  return chiave ? `file:${chiave}` : null;
};

/** Letto davvero, non fallito, riconoscibile. */
export const eSalvabile = (elemento) => {
  if (!elemento || typeof elemento !== 'object' || Array.isArray(elemento)) return false;
  if (elemento.estrazioneFallita === true) return false;
  if (elemento.errorMessage) return false;
  if (typeof elemento.extractedText !== 'string' || !elemento.extractedText.trim()) return false;
  return identitaElemento(elemento) !== null;
};

const NON_SALVABILE = new Error('non salvabile');

const eBinario = (valore) =>
  (typeof Blob !== 'undefined' && valore instanceof Blob) ||
  (typeof ArrayBuffer !== 'undefined' && (valore instanceof ArrayBuffer || ArrayBuffer.isView(valore)));

const eOggettoSemplice = (valore) => {
  const prototipo = Object.getPrototypeOf(valore);
  return prototipo === Object.prototype || prototipo === null;
};

/** Copia dei soli dati semplici, con la semantica di JSON; lancia davanti a un File, un Blob o un oggetto di classe. */
const copiaDati = (valore, profondita) => {
  if (profondita > PROFONDITA_MAX) throw NON_SALVABILE;
  if (valore === null) return null;
  switch (typeof valore) {
    case 'string':
    case 'boolean':
      return valore;
    case 'number':
      return Number.isFinite(valore) ? valore : null;
    case 'object':
      break;
    default:
      return undefined; // funzioni, undefined, simboli: fuori, come fa JSON
  }
  if (eBinario(valore)) throw NON_SALVABILE;
  if (valore instanceof Date) return Number.isNaN(valore.getTime()) ? null : valore.toISOString();
  if (Array.isArray(valore)) {
    return valore.map((voce) => {
      const copia = copiaDati(voce, profondita + 1);
      return copia === undefined ? null : copia;
    });
  }
  if (!eOggettoSemplice(valore)) throw NON_SALVABILE;
  const copia = {};
  Object.keys(valore).forEach((campo) => {
    const dato = copiaDati(valore[campo], profondita + 1);
    if (dato !== undefined) copia[campo] = dato;
  });
  return copia;
};

/** La copia salvabile di un elemento della coda, o null se non si salva. */
export const pulisciElemento = (elemento) => {
  if (!eSalvabile(elemento)) return null;
  const sorgente = { ...elemento };
  // Le foto scattate insieme portano i File originali: servivano all'OCR,
  // la generazione non li legge.
  if (Array.isArray(sorgente.files) && sorgente.files.some(eBinario)) delete sorgente.files;
  try {
    return copiaDati(sorgente, 0);
  } catch (_) {
    return null;
  }
};

/** Le voci da scrivere, nell'ordine della coda e senza doppioni. */
export const vociDaSalvare = (coda) => {
  if (!Array.isArray(coda)) return [];
  const viste = new Set();
  const voci = [];
  coda.forEach((elemento) => {
    const pulito = pulisciElemento(elemento);
    if (!pulito) return;
    const identita = identitaElemento(pulito);
    if (!identita || viste.has(identita)) return;
    viste.add(identita);
    voci.push({ identita, elemento: pulito });
  });
  return voci;
};

const vociLette = (record, uid) => {
  if (!record || typeof record !== 'object' || !Array.isArray(record.voci)) return [];
  if (record.uid && record.uid !== uid) return [];
  return record.voci.filter((voce) =>
    voce &&
    typeof voce.identita === 'string' &&
    Number.isFinite(voce.salvatoIl) &&
    eSalvabile(voce.elemento) &&
    identitaElemento(voce.elemento) === voce.identita
  );
};

// ---------------------------------------------------------------------------
// Adattatori: IndexedDB in produzione, memoria nei test
// ---------------------------------------------------------------------------

/**
 * Adattatore in memoria, con lo stesso contratto di quello IndexedDB:
 * `leggi(chiave)`, `scrivi(chiave, valore)`, `cancella(chiave)`, `chiavi()`,
 * tutte asincrone. Copia i valori come farebbe il browser, cosi' un test non
 * puo' passare tenendo in mano lo stesso oggetto che ha scritto.
 */
export const creaAdattatoreInMemoria = () => {
  const dati = new Map();
  const copia = (valore) => (valore === undefined ? undefined : JSON.parse(JSON.stringify(valore)));
  return {
    leggi: async (chiave) => copia(dati.get(chiave)),
    scrivi: async (chiave, valore) => {
      dati.set(chiave, copia(valore));
    },
    cancella: async (chiave) => {
      dati.delete(chiave);
    },
    chiavi: async () => Array.from(dati.keys()),
  };
};

/** Adattatore IndexedDB, sottile: un archivio, chiave = uid. */
export const creaAdattatoreIndexedDB = (
  idb = typeof indexedDB !== 'undefined' ? indexedDB : undefined
) => {
  let apertura = null;

  const apri = () => {
    if (!idb) return Promise.reject(new Error('IndexedDB non disponibile'));
    if (!apertura) {
      apertura = new Promise((resolve, reject) => {
        const richiesta = idb.open(NOME_DB, VERSIONE_DB);
        richiesta.onupgradeneeded = () => {
          const db = richiesta.result;
          if (!db.objectStoreNames.contains(NOME_ARCHIVIO)) db.createObjectStore(NOME_ARCHIVIO);
        };
        richiesta.onsuccess = () => {
          const db = richiesta.result;
          // Un'altra scheda che aggiorna lo schema: si chiude e si riapre al prossimo giro.
          db.onversionchange = () => {
            db.close();
            apertura = null;
          };
          resolve(db);
        };
        richiesta.onerror = () => reject(richiesta.error || new Error('IndexedDB: apertura fallita'));
        richiesta.onblocked = () => reject(new Error('IndexedDB: apertura bloccata'));
      });
      apertura.catch(() => {
        apertura = null;
      });
    }
    return apertura;
  };

  const operazione = (modo, fai) =>
    apri().then(
      (db) =>
        new Promise((resolve, reject) => {
          let transazione;
          try {
            transazione = db.transaction(NOME_ARCHIVIO, modo);
          } catch (errore) {
            // Safari chiude la connessione sotto carico: la prossima volta si riapre.
            apertura = null;
            reject(errore);
            return;
          }
          const richiesta = fai(transazione.objectStore(NOME_ARCHIVIO));
          transazione.oncomplete = () => resolve(richiesta.result);
          transazione.onerror = () => reject(transazione.error || richiesta.error);
          transazione.onabort = () => reject(transazione.error || new Error('IndexedDB: transazione annullata'));
        })
    );

  return {
    leggi: (chiave) => operazione('readonly', (archivio) => archivio.get(chiave)),
    scrivi: (chiave, valore) => operazione('readwrite', (archivio) => archivio.put(valore, chiave)),
    cancella: (chiave) => operazione('readwrite', (archivio) => archivio.delete(chiave)),
    chiavi: () => operazione('readonly', (archivio) => archivio.getAllKeys()),
  };
};

// ---------------------------------------------------------------------------
// La coda salvata: scrivere, ripristinare, cancellare
// ---------------------------------------------------------------------------

export const creaCodaFileSalvata = ({ adattatore, ora = () => Date.now() } = {}) => {
  // Quando ogni file e' stato salvato la prima volta, per l'uid a cui
  // appartiene: la scadenza di 24 ore parte da li'.
  let primaVolta = { uid: null, mappa: new Map() };

  const mappaDi = (uid) => (primaVolta.uid === uid ? primaVolta.mappa : new Map());

  /** Scrive la coda di `uid`; una coda senza niente da salvare cancella la voce. */
  const salva = async (uid, coda) => {
    if (!uid) return;
    const voci = vociDaSalvare(coda);
    if (voci.length === 0) {
      primaVolta = { uid, mappa: new Map() };
      await adattatore.cancella(uid);
      return;
    }
    const adesso = ora();
    const precedente = mappaDi(uid);
    const mappa = new Map();
    const record = {
      versione: VERSIONE_FORMATO,
      uid,
      voci: voci.map(({ identita, elemento }) => {
        const salvatoIl = precedente.has(identita) ? precedente.get(identita) : adesso;
        mappa.set(identita, salvatoIl);
        return { identita, salvatoIl, elemento };
      }),
    };
    primaVolta = { uid, mappa };
    await adattatore.scrivi(uid, record);
  };

  /** Gli elementi ancora validi di `uid`, nell'ordine in cui erano. Rifiuta se la lettura fallisce. */
  const ripristina = async (uid) => {
    if (!uid) return [];
    try {
      const chiavi = (await adattatore.chiavi()) || [];
      await Promise.all(chiavi.filter((chiave) => chiave !== uid).map((chiave) => adattatore.cancella(chiave)));
    } catch (_) {
      // pulizia mancata: le code degli altri restano chiuse sotto il loro uid
    }
    const record = await adattatore.leggi(uid);
    const adesso = ora();
    const viste = new Set();
    const vive = [];
    vociLette(record, uid).forEach((voce) => {
      // Un orologio tornato indietro non allunga la vita di un file.
      const salvatoIl = Math.min(voce.salvatoIl, adesso);
      if (adesso - salvatoIl >= SCADENZA_MS || viste.has(voce.identita)) return;
      viste.add(voce.identita);
      vive.push({ ...voce, salvatoIl });
    });
    primaVolta = { uid, mappa: new Map(vive.map((voce) => [voce.identita, voce.salvatoIl])) };
    if (record && vive.length === 0) {
      try {
        await adattatore.cancella(uid);
      } catch (_) {
        // scaduta o illeggibile: al prossimo giro si riprova
      }
    }
    return vive.map((voce) => voce.elemento);
  };

  /**
   * Toglie la voce di `uid` dal disco e basta: se la coda si riscrive (un
   * «Genera» fallito), ogni file tiene la scadenza che aveva.
   */
  const cancella = async (uid) => {
    if (!uid) return;
    await adattatore.cancella(uid);
  };

  const cancellaTutto = async () => {
    primaVolta = { uid: null, mappa: new Map() };
    const chiavi = (await adattatore.chiavi()) || [];
    await Promise.all(chiavi.map((chiave) => adattatore.cancella(chiave)));
  };

  return { salva, ripristina, cancella, cancellaTutto };
};

// ---------------------------------------------------------------------------
// Il collegamento con lo store Redux e con l'account
// ---------------------------------------------------------------------------

let collegamentoAttivo = null;

/**
 * Collega la coda salvata allo store.
 *
 * - `osservaUtente(callback)` chiama `callback(uid | null)` a ogni cambio di
 *   account e restituisce la funzione per smettere (in produzione:
 *   onAuthStateChanged di Firebase). Non si usa `state.auth.user.uid`:
 *   diversi `setUser` lo rimpiazzano con dati di Firestore senza uid.
 * - Al primo uid noto si ripristina; da quel momento ogni cambio della coda
 *   si scrive.
 */
export const collegaCodaAlloStore = ({
  store,
  coda,
  osservaUtente,
  ritardoMs = RITARDO_SCRITTURA_MS,
  traccia = trackEvent,
  ora = () => Date.now(),
  finestra = typeof window !== 'undefined' ? window : undefined,
}) => {
  let uidAttivo = null;
  let ripristinatoPer = null;
  let turno = 0; // cresce a ogni cambio di account: un ripristino in volo per quello vecchio si scarta
  let timer = null;
  let ultimaCoda;
  let ultimaConsumata;
  // «Genera» e' partito e non ha ancora un esito: la copia e' stata tolta e
  // nessuna scrittura la rimette (vedi DURANTE «GENERA» in cima).
  let sospesa = false;

  const documento = () => (store.getState() || {}).document || {};

  const fermaTimer = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };

  const scriviOra = () => {
    fermaTimer();
    if (!uidAttivo || ripristinatoPer !== uidAttivo) return;
    const stato = documento();
    const consumata = stato.isProcessing === true;
    // Riuscita la generazione, la coda e' consumata: da qui la regola di sempre.
    if (sospesa && !consumata) return;
    sospesa = false;
    coda.salva(uidAttivo, consumata ? [] : stato.processedNotes).catch(() => {});
  };

  const programmaScrittura = (subito) => {
    if (subito) {
      scriviOra();
      return;
    }
    if (timer) return;
    timer = setTimeout(scriviOra, ritardoMs);
  };

  const suCambio = () => {
    const stato = documento();
    const codaAttuale = stato.processedNotes;
    const consumata = stato.isProcessing === true;
    if (codaAttuale === ultimaCoda && consumata === ultimaConsumata) return;
    ultimaCoda = codaAttuale;
    ultimaConsumata = consumata;
    // LA TRAPPOLA: prima del ripristino la coda vuota non e' una notizia.
    if (!uidAttivo || ripristinatoPer !== uidAttivo) return;
    const vuota = !Array.isArray(codaAttuale) || codaAttuale.length === 0;
    programmaScrittura(consumata || vuota);
  };

  const svuotaMemoria = () => {
    const { processedNotes } = documento();
    if (Array.isArray(processedNotes) && processedNotes.length > 0) {
      store.dispatch({ type: RESET_PROCESSED_NOTES });
    }
  };

  const ripristina = (uid, turnoDiPartenza) => {
    coda
      .ripristina(uid)
      .then((elementi) => {
        if (turnoDiPartenza !== turno) return;
        const presenti = new Set(
          (documento().processedNotes || []).map(identitaElemento).filter(Boolean)
        );
        const nuovi = elementi.filter((elemento) => !presenti.has(identitaElemento(elemento)));
        nuovi.forEach((elemento) => store.dispatch({ type: ADD_PROCESSED_NOTE, payload: elemento }));
        // Senza questo la card mostra i file ma non la riga con «Genera».
        if (nuovi.length > 0) store.dispatch({ type: SET_GENERATE, payload: true });
        ripristinatoPer = uid;
        programmaScrittura(false);
        if (nuovi.length > 0) {
          try {
            traccia(EVENTO_RIPRISTINO, { category: 'upload', files_count: nuovi.length });
          } catch (_) {
            // l'analytics non deve mai toccare la coda
          }
        }
      })
      .catch(() => {
        // Lettura fallita: in questa sessione non si scrive, cosi' una coda
        // vuota non puo' cancellare un salvato che non si e' riusciti a leggere.
      });
  };

  const suUtente = (uid) => {
    const nuovo = uid || null;
    if (nuovo === uidAttivo) return;
    const precedente = uidAttivo;
    fermaTimer();
    turno += 1;
    uidAttivo = nuovo;
    ripristinatoPer = null;
    sospesa = false;
    // La coda in memoria era dell'account di prima: non la vede il prossimo
    // e non finisce sotto il suo uid. Quella salvata resta sotto il vecchio.
    if (precedente) svuotaMemoria();
    if (nuovo) ripristina(nuovo, turno);
  };

  /** Uscita dall'account: niente piu' scritture, coda in memoria e salvata cancellate. */
  const esci = () => {
    fermaTimer();
    turno += 1;
    ripristinatoPer = null;
    sospesa = false;
    svuotaMemoria();
    return coda.cancellaTutto().catch(() => {});
  };

  /** «Genera» parte: via la copia, e nessuna scrittura finche' non c'e' un esito. Non rifiuta mai. */
  const sospendi = () => {
    fermaTimer();
    sospesa = true;
    if (!uidAttivo || ripristinatoPer !== uidAttivo) return Promise.resolve();
    return coda.cancella(uidAttivo).catch(() => {});
  };

  /** «Genera» e' fallito: la coda torna sul disco com'e' adesso (vuota se e' stata consumata). */
  const riprendi = () => {
    sospesa = false;
    scriviOra();
  };

  // Chi chiude o ricarica la pagina mentre una scrittura aspetta il suo turno.
  const suUscitaPagina = () => {
    if (timer) scriviOra();
  };

  const smettiStore = store.subscribe(suCambio);
  suCambio();
  const smettiUtente = osservaUtente(suUtente);
  if (finestra && typeof finestra.addEventListener === 'function') {
    finestra.addEventListener('pagehide', suUscitaPagina);
  }

  const collegamento = {
    esci,
    sospendi,
    riprendi,
    scollega: () => {
      fermaTimer();
      smettiStore();
      if (typeof smettiUtente === 'function') smettiUtente();
      if (finestra && typeof finestra.removeEventListener === 'function') {
        finestra.removeEventListener('pagehide', suUscitaPagina);
      }
      if (collegamentoAttivo === collegamento) collegamentoAttivo = null;
    },
  };
  collegamentoAttivo = collegamento;
  return collegamento;
};

/** Da index.js: coda salvata in IndexedDB, collegata allo store e all'account. */
export const avviaCodaFileSalvata = ({ store, osservaUtente }) => {
  try {
    const coda = creaCodaFileSalvata({ adattatore: creaAdattatoreIndexedDB() });
    return collegaCodaAlloStore({ store, coda, osservaUtente });
  } catch (_) {
    return null;
  }
};

/** Si risolve quando `promessa` si chiude, comunque vada, o dopo `ms`: mai piu' tardi, mai con un rifiuto. */
const entroIlTempo = (promessa, ms) =>
  new Promise((resolve) => {
    const scaduto = setTimeout(resolve, ms);
    const fine = () => {
      clearTimeout(scaduto);
      resolve();
    };
    Promise.resolve(promessa).then(fine, fine);
  });

/**
 * Da handleGenerateExam, al clic su «Genera», prima del primo documento
 * (STU-424, DURANTE «GENERA» in cima): la copia salvata si cancella e non si
 * riscrive finche' la generazione non ha un esito. Si aspetta perche' nessun
 * documento nasca mentre la copia c'e' ancora, ma al massimo
 * ATTESA_CANCELLAZIONE_MS. Non lancia e non rifiuta mai.
 */
export const sospendiCodaFileSalvata = () => {
  try {
    if (!collegamentoAttivo) return Promise.resolve();
    return entroIlTempo(collegamentoAttivo.sospendi(), ATTESA_CANCELLAZIONE_MS);
  } catch (_) {
    return Promise.resolve();
  }
};

/**
 * Dal catch di handleGenerateExam: la generazione e' fallita e i file sono
 * ancora da generare, quindi la coda torna sul disco com'e' adesso in Redux e
 * sopravvive di nuovo a una ricarica. Non lancia mai.
 */
export const riprendiCodaFileSalvata = () => {
  try {
    if (collegamentoAttivo) collegamentoAttivo.riprendi();
  } catch (_) {
    // la coda salvata non deve mai cambiare l'esito di «Genera»
  }
};

/**
 * Da useSignOut: all'uscita dall'account non resta niente, ne' in memoria ne'
 * sul disco. Non lancia e non rifiuta mai: l'uscita non deve dipendere da
 * IndexedDB.
 */
export const svuotaCodaFileSalvata = () => {
  try {
    if (collegamentoAttivo) return collegamentoAttivo.esci();
    return creaCodaFileSalvata({ adattatore: creaAdattatoreIndexedDB() })
      .cancellaTutto()
      .catch(() => {});
  } catch (_) {
    return Promise.resolve();
  }
};
